import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../../lib/api";
import { FSelect } from "../engagement/FormControls";
import { SignatureStamp } from "../../components/SignaturePad";
import { QuickClientModal } from "../../components/QuickClientModal";
import { Search, X, Plus } from "lucide-react";

export const CLINICAL_TABS = [
  { label: "All Documents", to: "/clinical/documents" },
  { label: "Progress Notes", to: "/clinical/progress-notes" },
  { label: "Treatment Plans", to: "/clinical/treatment-plans" },
  { label: "Consent Forms", to: "/clinical/consents" },
  { label: "Assessments", to: "/clinical/assessments" },
  { label: "Crisis Plans", to: "/clinical/crisis-plans" },
];

export const TYPE_LABELS = {
  progress_note: "Progress Note",
  treatment_plan: "Treatment Plan",
  consent: "Consent Form",
  assessment: "Assessment",
  crisis_plan: "Crisis or Safety Plan",
};

export function ClinicalTabs() {
  return (
    <div className="mb-6 flex flex-wrap rounded-lg bg-[#F1EEE7]" data-testid="clinical-tabs">
      {CLINICAL_TABS.map((t) => (
        <NavLink key={t.to} to={t.to} data-testid={`clinical-tab-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={({ isActive }) => `whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors rounded-lg ${isActive ? "bg-[#D9C7B2] text-[#3E2723]" : "text-[#84675B] hover:text-[#6D4C41]"}`}>
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

export function useClients() {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  return clients;
}

/**
 * Typeahead client picker. Backed by the same `clients` list the caller already has,
 * but renders as a search box so a directory of 50k clients does not force a scroll.
 * Contract kept identical to the old dropdown: props {clients, value, onChange, testId, disabled}.
 * Extra optional props:
 *   allowCreate    - default true. Shows a "+ New Client" footer button that opens the
 *                    QuickClientModal so front desk can create a chart without leaving the page.
 *   onClientCreated - optional. fires (newClient) after a successful quick-create so the parent
 *                     can push the new record into its local `clients` state.
 */
export function ClientSelect({ clients, value, onChange, testId, disabled, allowCreate = true, onClientCreated }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [extras, setExtras] = useState([]); // locally-created clients (fallback if parent does not sync)
  const boxRef = useRef(null);
  const mergedClients = useMemo(() => {
    // Prefer parent list, then fill in any local extras that the parent has not yet synced
    const ids = new Set(clients.map((c) => c && c.id).filter(Boolean));
    const tail = extras.filter((c) => c && c.id && !ids.has(c.id));
    return [...clients, ...tail];
  }, [clients, extras]);
  const selected = mergedClients.find((c) => c.id === value);

  useEffect(() => {
    // Keep the input text in sync when the picker is not being typed in.
    if (!open) setQ(selected ? `${selected.first_name} ${selected.last_name}${selected.mrn ? ` - ${selected.mrn}` : ""}` : "");
  }, [selected, open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const norm = (s) => (s || "").toString().toLowerCase();
  // Deduplicate defensively - the source list can contain the same client twice
  // (e.g. tenant fan-out or stale cache) which would trigger the React duplicate-key warning.
  const dedupClients = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of mergedClients) {
      if (!c || !c.id || seen.has(c.id)) continue;
      seen.add(c.id); out.push(c);
    }
    return out;
  }, [mergedClients]);
  const query = norm(q).trim();
  const results = query
    ? dedupClients.filter((c) => {
        const hay = `${c.first_name || ""} ${c.last_name || ""} ${c.mrn || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
        return query.split(/\s+/).every((tok) => hay.includes(tok));
      }).slice(0, 20)
    : dedupClients.slice(0, 20);

  const pick = (c) => {
    onChange(c.id);
    setQ(`${c.first_name} ${c.last_name}${c.mrn ? ` - ${c.mrn}` : ""}`);
    setOpen(false);
  };

  const handleCreated = (newClient) => {
    // Track locally so the selected label stays populated even if parent has not re-fetched yet
    setExtras((s) => [...s, newClient]);
    if (typeof onClientCreated === "function") onClientCreated(newClient);
    onChange(newClient.id);
    setQ(`${newClient.first_name} ${newClient.last_name}${newClient.mrn ? ` - ${newClient.mrn}` : ""}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          type="text"
          data-testid={testId}
          value={q}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search by name, MRN, email, or phone"
          autoComplete="off"
          className={`w-full rounded-lg border border-[#E5DFD3] bg-white pl-9 pr-9 py-2 text-sm text-[#3E2723] outline-none focus:border-[#8D6E63] ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        />
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
        {value && !disabled && (
          <button type="button" data-testid={`${testId || "client-select"}-clear`} onClick={() => { onChange(""); setQ(""); setOpen(false); }}
            aria-label="Clear client selection"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={14} /></button>
        )}
      </div>
      {open && !disabled && (
        <div data-testid={`${testId || "client-select"}-dropdown`}
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="p-3 text-center text-xs text-[#7B6157]">No clients match &quot;{q}&quot;</div>
          ) : results.map((c) => {
            const active = c.id === value;
            return (
              <button key={c.id} type="button" data-testid={`${testId || "client-select"}-option-${c.id}`}
                onClick={() => pick(c)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${active ? "bg-[#F5F2EB] text-[#3E2723] font-semibold" : "hover:bg-[#FDFCF8] text-[#3E2723]"}`}>
                <div className="flex items-center justify-between gap-3">
                  <span>{c.first_name} {c.last_name}</span>
                  <span className="text-[10px] text-[#7B6157] tabular-nums">{c.mrn || c.hrn || ""}</span>
                </div>
                {(c.email || c.phone) && (
                  <div className="mt-0.5 text-[11px] text-[#7B6157] truncate">{c.email || ""}{c.email && c.phone ? " · " : ""}{c.phone || ""}</div>
                )}
              </button>
            );
          })}
          {dedupClients.length > results.length && !query && (
            <div className="border-t border-[#EFEBE0] p-2 text-center text-[11px] text-[#7B6157]">{dedupClients.length - results.length} more; type to narrow</div>
          )}
          {allowCreate && (
            <button type="button"
              data-testid={`${testId || "client-select"}-quick-add`}
              onMouseDown={(e) => { e.preventDefault(); setQuickOpen(true); setOpen(false); }}
              className="sticky bottom-0 w-full border-t border-[#EFEBE0] bg-[#FDFCF8] px-3 py-2 text-left text-xs font-bold text-[#4E342E] hover:bg-[#F5F2EB]">
              <span className="inline-flex items-center gap-1.5"><Plus size={12} /> New Client{query ? ` - "${q}"` : ""}</span>
            </button>
          )}
        </div>
      )}
      {allowCreate && (
        <QuickClientModal open={quickOpen} onClose={() => setQuickOpen(false)}
          onCreated={handleCreated} initialName={q} />
      )}
    </div>
  );
}

export function SignatureDisplay({ sig, label }) {
  if (!sig) return null;
  return (
    <div className="rounded-xl border border-[#E5DFD3] bg-[#FDFCF8] p-3 min-w-[220px]" data-testid="signature-display">
      <SignatureStamp sig={sig} label={label} />
    </div>
  );
}
